import { useState, useCallback, useRef } from 'react';
import { generateBookingPaymentLink } from '../services/subscriberWalletService';

// Shared by the medicine and diagnostic carts. `prepare` persists the cart as shown on screen
// (the existing submit chain) so the link is generated for exactly what ops is looking at.
export const useBookingPaymentLink = (currentRole) => {
  const [generatingLink, setGeneratingLink] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  // State updates are async, so a fast double click would pass a `generatingLink` check; the
  // ref blocks the second click synchronously.
  const inFlight = useRef(false);

  const generateLink = useCallback(async (orderId, prepare, showNotification) => {
    if (inFlight.current || !orderId) return null;
    inFlight.current = true;
    setGeneratingLink(true);
    try {
      if (prepare && !(await prepare())) return null;

      const response = await generateBookingPaymentLink(currentRole, orderId);
      if (response?.metadata?.api_status !== true) {
        throw new Error(response?.msg?.error || 'Failed to generate payment link');
      }
      const link = response.msg;
      setPaymentLink(link);
      const via = link.channels?.includes('whatsapp') ? 'SMS and WhatsApp' : 'SMS';
      const amount = Number(link.amount || 0).toFixed(2);
      showNotification(
        `${link.reused ? 'Payment link re-sent' : 'Payment link sent'} via ${via} · ₹${amount}`,
        'success'
      );
      return link;
    } catch (error) {
      showNotification(error.message || 'Failed to generate payment link', 'error');
      return null;
    } finally {
      inFlight.current = false;
      setGeneratingLink(false);
    }
  }, [currentRole]);

  return { generatingLink, paymentLink, generateLink };
};
